import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronDown, Check, Globe } from 'lucide-react';
import { apiClient } from '../../api/client';

export interface Branch {
  id: string;
  name: string;
  street: string | null;
  city: string | null;
  zip: string | null;
  phone: string | null;
  email: string | null;
  isActive: boolean;
}

function loadActiveBranchId(): string | null {
  return sessionStorage.getItem('active_branch_id');
}

export function useBranches() {
  return useQuery<Branch[]>({
    queryKey: ['branches'],
    queryFn: () => apiClient.get('/branches').then(r => r.data.data),
    staleTime: 60_000,
  });
}

interface Props {
  /** Pokud true, uživatel má pevnou pobočku z tokenu a přepínač se nezobrazí */
  userBranchId: string | null;
}

export default function BranchSwitcher({ userBranchId }: Props) {
  const qc = useQueryClient();
  const { data: branches = [] } = useBranches();
  const [open, setOpen] = useState(false);
  const [activeBranchId, setActiveBranchId] = useState<string | null>(loadActiveBranchId);

  // Uživatel s pevnou pobočkou — switcher neskryjeme, ale zobrazíme jen badge s jeho pobočkou
  if (userBranchId) {
    const branch = branches.find(b => b.id === userBranchId);
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 font-medium">
        <Building2 className="h-3.5 w-3.5 shrink-0" />
        <span className="hidden sm:block">{branch?.name ?? 'Pobočka'}</span>
      </div>
    );
  }

  const active = activeBranchId ? branches.find(b => b.id === activeBranchId) : null;
  const label  = active ? active.name : 'Všechny pobočky';

  function switchBranch(id: string | null) {
    setActiveBranchId(id);
    if (id) sessionStorage.setItem('active_branch_id', id);
    else    sessionStorage.removeItem('active_branch_id');
    setOpen(false);
    // Invalidate všechny queries tak se data refetchnou s novým X-Branch-Id
    qc.invalidateQueries();
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:border-blue-300 hover:text-blue-700 transition-colors"
      >
        {active ? <Building2 className="h-3.5 w-3.5 shrink-0 text-blue-600" /> : <Globe className="h-3.5 w-3.5 shrink-0 text-gray-400" />}
        <span className="hidden sm:block max-w-[120px] truncate">{label}</span>
        <ChevronDown className="h-3 w-3 text-gray-400 shrink-0" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20">
            <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100 mb-1">
              Zobrazit pobočku
            </div>
            <button
              onClick={() => switchBranch(null)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <span className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-gray-400" />
                Všechny pobočky
              </span>
              {!activeBranchId && <Check className="h-3.5 w-3.5 text-blue-600" />}
            </button>
            {branches.filter(b => b.isActive).map(branch => (
              <button
                key={branch.id}
                onClick={() => switchBranch(branch.id)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-blue-500" />
                  <span className="truncate">{branch.name}</span>
                </span>
                {activeBranchId === branch.id && <Check className="h-3.5 w-3.5 text-blue-600 shrink-0" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
